import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { combineLatest, Observable, of, throwError } from 'rxjs';
import { UserService, User } from '@hub-services/user.service';
import { AuthRedirectInitiatedError } from './auth-redirect-initiated-error';
import { Partner } from '@hub-services/partner';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { getSessionData } from '@hub-store/session/session.selectors';
import { getSelectedPartner } from '@hub-store/selected-partner/selected-partner.selectors';
import { RoleType } from '../admin/role-type.enum';
import { WINDOW } from '@hub-shared/window.providers';
import { SessionsService } from '@hub-services/sessions.service';
import { PartnersService } from '@hub-services/partners.service';
import { setSessionData } from '@hub-store/session/session.actions';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class AuthGuard {
  constructor(
    private router: Router,
    private userService: UserService,
    private store: Store,
    @Inject(WINDOW) private window: Window,
    private sessionsService: SessionsService,
    private authService: AuthService,
    private partnersService: PartnersService,
  ) { }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    const url: string = state.url;
    return this.authService.waitForIdle().pipe(
      switchMap(() => combineLatest([
        this.store.select(getSessionData).pipe(take(1)),
        this.userService.getUser().pipe(take(1)),
        this.store.select(getSelectedPartner).pipe(take(1)),
      ])),
      switchMap(([session, user, partner]: [any, User | null, Partner | null]) => {
        const session$ = (session === null || session === undefined)
          ? this.sessionsService.getSession(false).pipe(
              tap((sessionData: any) => this.store.dispatch(setSessionData({ data: sessionData }))))
          : of(session);

        return session$.pipe(
          switchMap((sessionData: any) =>
            this.resolvePartner(sessionData, partner ?? undefined).pipe(
              map((resolvedPartner) => this.checkPermisions(url, sessionData, user as User, resolvedPartner))
            )
          )
        );
      }),
      catchError((err: unknown) => {
        if (err instanceof AuthRedirectInitiatedError) {
          return of(false);
        }
        return throwError(() => err);
      })
    );
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    return this.canActivate(route, state);
  }

  // Guards run before resolvers, so on a hard load the selected partner isn't
  // seeded yet and tier checks would wrongly deny entitled users. Fetch it here
  // when missing (mirrors the session fallback above).
  private resolvePartner(session: any, partner?: Partner): Observable<Partner | undefined> {
    if (partner || !session?.partner_id) {
      return of(partner);
    }
    return this.partnersService.getPartner(session.partner_type, session.partner_id).pipe(
      take(1),
      catchError(() => of(undefined)),
    );
  }

  private checkPermisions(url: string, session: any, user: User, partner?: Partner): boolean | UrlTree {

    if (!user) {
      return this.router.createUrlTree(['/access-denied']);
    }

    const path = url.split('/')[1];
    let canAccess = false;

    if (user.role === RoleType.ADMIN && !session?.partner_id) {
      this.window.location.href = '/admin/airlines';
      return false;
    }
    if (user.role !== RoleType.ADMIN && !user.accepted_terms) {
      this.window.location.href = '/terms';
      return false;
    }

    switch (path) {
      case 'channels': {
        canAccess = this.userService.getPermissionShowAirlineChannels(user, partner);
        break;
      }
      case 'upas': {
        canAccess = this.userService.getPermissionShowAirlineUpas(user, partner);
        break;
      }
      case 'utas': {
        canAccess = this.userService.getPermissionShowAirlineUtas(user, partner);
        break;
      }
      case 'utasv1': {
        canAccess = this.userService.getPermissionDisplayChangePartner(user);
        break;
      }
      default: {
        canAccess = false;
      }
    }

    return canAccess ? true : this.router.createUrlTree(['/access-denied']);
  }
}
